import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Input, Modal, Space, Table, message, Tooltip } from 'antd';
import { EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { Configuration } from '../../types/config.types';
import { configurationService } from '../../services/configurationService';
import { ColumnsType } from 'antd/es/table';
import { usePermissions } from '../../hooks/usePermissions';
import ContentSkeleton from '../../components/common/ContentSkeleton';
import ErrorState from '../../components/common/ErrorState';

const ConfigurationList: React.FC = () => {
    const [configurations, setConfigurations] = useState<Configuration[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState<Configuration | null>(null);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [form] = Form.useForm();
    const { hasPermission } = usePermissions();

    const fetchConfigurations = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await configurationService.getConfigurations();
            if (response.status === 'SUCCESS') {
                setConfigurations(response.data);
            } else {
                setError(response.message || 'Failed to fetch configurations');
                message.error(response.message || 'Failed to fetch configurations');
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to fetch configurations';
            setError(errorMessage);
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchConfigurations();
    }, []);

    const handleRefresh = () => {
        fetchConfigurations();
    };

    const handleEdit = (record: Configuration) => {
        setSelectedConfig(record);
        form.setFieldsValue({
            value: record.value,
            description: record.description
        });
        setEditModalVisible(true);
    };

    const handleCancel = () => {
        form.resetFields();
        setSelectedConfig(null);
        setEditModalVisible(false);
    };

    const handleUpdate = async () => {
        try {
            const values = await form.validateFields();
            if (!selectedConfig) return;

            setUpdateLoading(true);
            const request = {
                key: selectedConfig.key,
                value: values.value.trim(),
                description: values.description?.trim()
            };

            const response = await configurationService.updateConfiguration(request);
            if (response.status === 'SUCCESS') {
                message.success('Configuration updated successfully');
                handleCancel();
                fetchConfigurations();
            } else {
                message.error(response.message || 'Failed to update configuration');
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to update configuration';
            message.error(errorMessage);
        } finally {
            setUpdateLoading(false);
        }
    };

    const columns: ColumnsType<Configuration> = [
        {
            title: 'Key',
            dataIndex: 'key',
            key: 'key',
            width: '20%',
            ellipsis: true
        },
        {
            title: 'Value',
            dataIndex: 'value',
            key: 'value',
            width: '20%',
            ellipsis: true
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            width: '40%',
            ellipsis: true
        },
        {
            title: 'Type',
            key: 'type',
            width: '10%',
            render: (_, record) => (
                <span>{record.isDefault ? 'Default' : 'Custom'}</span>
            )
        },
        {
            title: 'Action',
            key: 'action',
            width: '10%',
            render: (_, record) => (
                <Space>
                    {hasPermission('UPDATE_CONFIGURATION') && (
                        <Tooltip title="Edit Configuration">
                            <Button
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => handleEdit(record)}
                            />
                        </Tooltip>
                    )}
                </Space>
            )
        }
    ];

    if (error) {
        return (
            <ErrorState
                error={error}
                onRetry={fetchConfigurations}
            />
        );
    }

    if (loading && configurations.length === 0) {
        return <ContentSkeleton />;
    }

    return (
        <Card
            title="System Configurations"
            extra={
                <Space>
                    <Tooltip title="Refresh">
                        <Button
                            type="text"
                            icon={<ReloadOutlined />}
                            onClick={handleRefresh}
                            loading={loading}
                        />
                    </Tooltip>
                </Space>
            }
        >
            <Table
                columns={columns}
                dataSource={configurations}
                rowKey="key"
                loading={loading}
                pagination={{
                    total: configurations.length,
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} configurations`,
                    pageSizeOptions: ['10', '20', '50']
                }}
                scroll={{ x: 800 }}
            />

            <Modal
                title="Edit Configuration"
                open={editModalVisible}
                onOk={handleUpdate}
                onCancel={handleCancel}
                okText="Update"
                cancelText="Cancel"
                confirmLoading={updateLoading}
                maskClosable={false}
                width={700}
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{ value: '', description: '' }}
                >
                    <Form.Item
                        label="Key"
                    >
                        <Input 
                            value={selectedConfig?.key} 
                            disabled 
                            style={{ backgroundColor: '#f5f5f5' }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="value"
                        label="Value"
                        rules={[
                            { required: true, message: 'Please input value!' },
                            { whitespace: true, message: 'Value cannot be empty!' }
                        ]}
                    >
                        <Input.TextArea 
                            rows={3}
                            placeholder="Enter configuration value"
                            maxLength={1000}
                            showCount
                        />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                        rules={[
                            { whitespace: true, message: 'Description cannot be empty!' }
                        ]}
                    >
                        <Input.TextArea 
                            rows={4}
                            placeholder="Enter configuration description"
                            maxLength={2000}
                            showCount
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default ConfigurationList; 